import { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'esd_tour_v2';

type Step = {
  /** CSS selector OR null for a centred intro card (no element to highlight). */
  selector: string | null;
  titleKey: string;
  textKey: string;
};

const STEPS: Step[] = [
  { selector: null,                                titleKey: 'tour.welcome_title',     textKey: 'tour.welcome_text' },
  { selector: '[data-tour="new-envelope"]',        titleKey: 'tour.new_envelope_title', textKey: 'tour.new_envelope_text' },
  { selector: '[data-tour="documents"]',           titleKey: 'tour.documents_title',    textKey: 'tour.documents_text' },
  { selector: '[data-tour="templates"]',           titleKey: 'tour.templates_title',    textKey: 'tour.templates_text' },
  { selector: '[data-tour="settings-branding"]',   titleKey: 'tour.branding_title',     textKey: 'tour.branding_text' },
  { selector: '[data-tour="lang-switcher"]',       titleKey: 'tour.lang_title',         textKey: 'tour.lang_text' },
  { selector: null,                                titleKey: 'tour.done_title',         textKey: 'tour.done_text' },
];

type Rect = { top: number; left: number; width: number; height: number };

/**
 * Interactive product tour — highlights real UI elements with a blurred
 * cutout overlay and a popover next to each target.
 *
 * Render once on first visit; reads/writes localStorage flag.
 */
export function InteractiveTour({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;
  const isIntro = current.selector === null;

  // Locate the target element when step changes, and update on resize / scroll.
  useLayoutEffect(() => {
    if (!current.selector) {
      setRect(null);
      return;
    }
    const measure = () => {
      const el = document.querySelector<HTMLElement>(current.selector!);
      if (!el) {
        setRect(null);
        return;
      }
      // Scroll the element into view so the user actually sees it being highlighted.
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      const r = el.getBoundingClientRect();
      const PAD = 6;
      setRect({
        top: r.top - PAD,
        left: r.left - PAD,
        width: r.width + PAD * 2,
        height: r.height + PAD * 2,
      });
    };
    measure();
    const id = window.setTimeout(measure, 320); // re-measure after scroll
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [current.selector]);

  // Lock body scroll while the tour is active.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  function dismiss() {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // localStorage disabled — accept reshow next session.
    }
    onClose();
  }

  // Tooltip position: prefer to the right of the highlight, fall back to below.
  function tooltipStyle(): React.CSSProperties {
    if (!rect) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    const TOOLTIP_W = 360;
    const TOOLTIP_H_EST = 220;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Try right of element.
    if (rect.left + rect.width + TOOLTIP_W + 16 < vw) {
      return {
        top: Math.min(vh - TOOLTIP_H_EST - 12, Math.max(12, rect.top)),
        left: rect.left + rect.width + 14,
        width: TOOLTIP_W,
      };
    }
    // Fall back to below.
    return {
      top: rect.top + rect.height + 12,
      left: Math.min(vw - TOOLTIP_W - 12, Math.max(12, rect.left)),
      width: TOOLTIP_W,
    };
  }

  return (
    <div className="fixed inset-0 z-50" aria-modal="true" role="dialog">
      {/* Backdrop with cutout — built from 4 dimmed rectangles around the target. */}
      {rect ? (
        <Cutout rect={rect} />
      ) : (
        <div
          className="absolute inset-0"
          style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(3px)' }}
        />
      )}

      {/* Highlight ring on top of the element */}
      {rect && (
        <div
          className="absolute pointer-events-none"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            borderRadius: 10,
            boxShadow: '0 0 0 3px #2563EB, 0 0 0 6px rgba(37,99,235,0.25), 0 8px 24px rgba(0,0,0,0.18)',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      )}

      {/* Tooltip / step card */}
      <div
        className="absolute bg-white rounded-xl shadow-2xl p-5 max-w-[360px]"
        style={{
          ...tooltipStyle(),
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase font-semibold tracking-wider text-primary">
            {t('tour.step_of', { current: step + 1, total: STEPS.length })}
          </div>
          <button
            onClick={dismiss}
            className="text-muted hover:text-gray-900 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <h3 className={`font-semibold text-gray-900 mb-2 ${isIntro ? 'text-xl' : 'text-base'}`}>
          {t(current.titleKey)}
        </h3>
        <p className="text-[13.5px] text-gray-700 leading-relaxed">
          {t(current.textKey)}
        </p>

        {/* Step dots */}
        <div className="flex gap-1 justify-center mt-4">
          {STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? 'bg-primary w-6' : 'bg-gray-300 w-1.5 hover:bg-gray-400'
              }`}
              aria-label={`Step ${i + 1}`}
            />
          ))}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
          {isFirst ? (
            <button onClick={dismiss} className="text-[13px] text-muted hover:text-gray-900">
              {t('tour.skip')}
            </button>
          ) : (
            <button
              onClick={() => setStep((s) => s - 1)}
              className="text-[13px] text-gray-700 hover:text-gray-900"
            >
              {t('tour.back')}
            </button>
          )}
          {isLast ? (
            <button onClick={dismiss} className="btn btn-primary btn-sm">
              {t('tour.done')}
            </button>
          ) : (
            <button onClick={() => setStep((s) => s + 1)} className="btn btn-primary btn-sm">
              {t('tour.next')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Renders four dimmed+blurred rectangles around the highlight rect. */
function Cutout({ rect }: { rect: Rect }) {
  const dim: React.CSSProperties = {
    position: 'absolute',
    background: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(3px)',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  };
  return (
    <>
      <div style={{ ...dim, top: 0, left: 0, right: 0, height: rect.top }} />
      <div style={{ ...dim, top: rect.top, left: 0, width: rect.left, height: rect.height }} />
      <div
        style={{
          ...dim,
          top: rect.top,
          left: rect.left + rect.width,
          right: 0,
          height: rect.height,
        }}
      />
      <div
        style={{
          ...dim,
          top: rect.top + rect.height,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />
    </>
  );
}

export function useShouldShowTour(): [boolean, () => void] {
  const [show, setShow] = useState(false);
  useEffect(() => {
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) setShow(true);
    } catch {
      // localStorage off — skip.
    }
  }, []);
  return [show, () => setShow(false)];
}
